/**
 * Group mutation operations (create, update, delete)
 */
import {store} from '@/store';
import {userAtom} from '@/store/user';
import {logger} from '@/lib/logger';
import {groupsAtom, selectedGroupAtom} from '@/store/groups';
import type {Group, GroupData, GroupsState} from '@/types/groups';
import {getGroupById} from './fetch';
import {fetchWithTimeout, buildMbApiUrl} from '@/util/request-utils';

const log = logger.tag('GroupsActions');

/**
 * Helper to upload group data to S3 using presigned URLs
 */
async function uploadGroupDataToS3(
    groupId: number,
    groupData: GroupData,
    authToken: string
): Promise<void> {
    const storeKeyUrl = buildMbApiUrl(
        `/product_groups/${groupId}/store_key.json`,
        {
            auth_token: authToken,
        }
    );
    const presignedResponse = await fetchWithTimeout(storeKeyUrl);

    if (!presignedResponse.ok) {
        throw new Error(
            `Failed to get S3 presigned URLs: ${presignedResponse.status}`
        );
    }

    const presigned = await presignedResponse.json();
    log.debug('Got presigned URLs');

    const formData = new FormData();

    // IMPORTANT: Field order matters for S3!
    Object.entries(presigned.data.fields).forEach(([key, value]) => {
        if (value !== undefined) formData.append(key, String(value));
    });

    const dataKey = `${groupId}/data.json`;
    formData.append('key', dataKey);

    const jsonBlob = new Blob([JSON.stringify(groupData, null, 2)], {
        type: 'application/json',
    });
    formData.append('file', jsonBlob, dataKey);

    const uploadResponse = await fetchWithTimeout(presigned.data.url, {
        method: 'POST',
        body: formData,
    });

    if (!uploadResponse.ok && uploadResponse.status !== 204) {
        throw new Error(
            `Failed to upload group data to S3: ${uploadResponse.status}`
        );
    }
}

export async function updateProductGroup(
    productGroupId: string,
    data: {name: string; state: string}
) {
    const user = store.get(userAtom);
    const body = {name: data.name, state: data.state};
    const url = buildMbApiUrl(`/product_groups/${productGroupId}.json`, {
        auth_token: user?.auth_token,
    });
    const response = await fetchWithTimeout(url, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        let errorData;

        try {
            errorData = await response.json();
        } catch {
            errorData = {message: response.statusText};
        }

        throw new Error(
            `Failed to update product group: ${
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string should use fallback
                errorData.message || response.statusText
            }`
        );
    }

    try {
        const updatedGroup = await response.json();

        store.set(selectedGroupAtom, updatedGroup);

        return updatedGroup;
    } catch {
        const group = await getGroupById(parseInt(productGroupId, 10));

        store.set(selectedGroupAtom, group ?? null);

        return group;
    }
}

export async function deleteProductGroup(groupId: string) {
    const user = store.get(userAtom);

    if (!user?.auth_token) {
        throw new Error('User not authenticated');
    }

    // Use buildMbApiUrl for proper token handling (no URL encoding on MB tokens)
    const url = buildMbApiUrl(`/product_groups/${groupId}.json`, {
        auth_token: user.auth_token,
    });

    const response = await fetchWithTimeout(url, {method: 'DELETE'});

    if (!response.ok) {
        throw new Error(`Failed to delete group: ${response.status}`);
    }

    // Remove from store
    store.set(groupsAtom, (prev: GroupsState) => ({
        ...prev,
        items: prev.items.filter((g) => String(g.id) !== String(groupId)),
        total: prev.total - 1,
    }));

    // Clear selected group if it's the one being deleted
    const selectedGroup = store.get(selectedGroupAtom);
    if (selectedGroup && String(selectedGroup.id) === String(groupId)) {
        store.set(selectedGroupAtom, null);
    }
}

interface CreateNewGroupParams {
    name: string;
    groupData: GroupData;
    state?: 'hidden' | 'shared';
}

export async function createNewGroupFromEdited(
    params: CreateNewGroupParams
): Promise<Group> {
    const user = store.get(userAtom);

    if (!user?.auth_token) throw new Error('User not authenticated');

    const {name, groupData, state = 'hidden'} = params;
    const createUrl = buildMbApiUrl('/product_groups.json', {
        auth_token: user.auth_token,
    });
    const createResponse = await fetchWithTimeout(createUrl, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
            product_group: {
                name: name,
                state: state,
            },
        }),
    });

    if (!createResponse.ok) {
        const errorText = await createResponse.text();

        throw new Error(
            `Failed to create product group: ${createResponse.status} ${errorText}`
        );
    }

    const newGroup: Group = await createResponse.json();

    await uploadGroupDataToS3(newGroup.id, groupData, user.auth_token);

    return newGroup;
}

export async function overwriteGroupData(
    groupId: number,
    groupData: GroupData,
    groupName: string,
    groupState: string
): Promise<void> {
    const user = store.get(userAtom);

    if (!user?.auth_token) throw new Error('User not authenticated');

    // Step 1 & 2: Upload data.json to S3
    await uploadGroupDataToS3(groupId, groupData, user.auth_token);

    // Step 3: Update the product group to trigger thumbnail regeneration
    // This triggers the after_commit hook in the backend which regenerates the 3D thumbnail
    await updateProductGroup(String(groupId), {
        name: groupName,
        state: groupState,
    });
}
